// Documentation.jsx
//
// Super-admin doc viewer at /admin/documentation. Renders the
// markdown files committed under /docs (ARCHITECTURE.md +
// SUPER_ADMIN.md) with:
//   - Tab switcher between docs
//   - Sticky left TOC built from the doc's headings
//   - Search box that highlights matches
//
// Reads markdown from the backend at runtime so doc edits ship
// without rebuilding the frontend.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { get } from '../../api';
import { parseMarkdown, slugify } from './markdownRenderer';

const POLL_MS = 0; // no auto-refresh; reader pulls fresh on mount

function highlightInPlace(content, q) {
  if (!q || q.length < 2) return content;
  // Mark matches inline with U+2042 ASTERISM as a sentinel that we
  // re-split on after the markdown renderer is done — cleaner than
  // mutating the React tree. We just don't highlight inside code
  // fences (regex on whole content is good enough for the doc shape).
  const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')})`, 'gi');
  return content.replace(re, '⁂$1⁂');
}

function HighlightWrapper({ children }) {
  // Walk the rendered tree, find text nodes containing the sentinel,
  // and wrap them in <mark>. We do this in a post-render pass via a
  // simple deep walker.
  return children;
}

export default function Documentation() {
  const [list, setList] = useState([]);
  const [activeSlug, setActiveSlug] = useState(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState('');

  // Initial: list available docs, default to first.
  useEffect(() => {
    setLoading(true);
    get('/api/admin/documentation')
      .then((res) => {
        const docs = (res && res.data) || [];
        setList(docs);
        if (docs.length) setActiveSlug(docs[0].slug);
        setLoading(false);
      })
      .catch((e) => { setErr((e && (e.message || (e.response && e.response.data && e.response.data.error))) || 'Failed to load'); setLoading(false); });
  }, []);

  // Load the active doc's content.
  useEffect(() => {
    if (!activeSlug) return;
    setLoading(true);
    get(`/api/admin/documentation/${activeSlug}`)
      .then((res) => {
        setContent((res && res.data && res.data.content) || '');
        setLoading(false);
      })
      .catch((e) => { setErr((e && (e.message || (e.response && e.response.data && e.response.data.error))) || 'Failed to load doc'); setLoading(false); });
  }, [activeSlug]);

  const { nodes, toc } = useMemo(() => {
    if (!content) return { nodes: [], toc: [] };
    const piped = highlightInPlace(content, search);
    return parseMarkdown(piped);
  }, [content, search]);

  // Post-render: replace ⁂...⁂ sentinels with <mark>. We do this by
  // walking the produced React tree and intercepting strings. For
  // simplicity (and because the renderer already produced the tree)
  // we inject a CSS rule that styles the sentinel via a custom DOM
  // transform after mount. Cheaper: walk the React tree below.
  const renderedNodes = useMemo(() => {
    if (!search || search.length < 2) return nodes;
    const transform = (node) => {
      if (typeof node === 'string') {
        if (!node.includes('⁂')) return node;
        const parts = node.split(/(⁂[^⁂]+⁂)/g);
        return parts.map((p, i) => {
          if (/^⁂[^⁂]+⁂$/.test(p)) {
            return <mark key={i} style={{ background: '#fef3c7', padding: '0 1px' }}>{p.slice(1, -1)}</mark>;
          }
          return p;
        });
      }
      if (Array.isArray(node)) return node.map(transform);
      if (node && node.props && node.props.children !== undefined) {
        const newChildren = Array.isArray(node.props.children)
          ? node.props.children.map(transform)
          : transform(node.props.children);
        return React.cloneElement(node, {}, newChildren);
      }
      return node;
    };
    return nodes.map(transform);
  }, [nodes, search]);

  const onClickToc = useCallback((slug) => (e) => {
    e.preventDefault();
    const el = document.getElementById(slug);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <div style={{ display: 'flex', minHeight: 'calc(100vh - 60px)', background: '#fff' }}>
      {/* Left: tab switcher + TOC */}
      <aside style={{ width: 280, borderRight: '1px solid #eee', padding: '20px 16px', position: 'sticky', top: 0, alignSelf: 'flex-start', height: 'calc(100vh - 60px)', overflowY: 'auto' }}>
        <h3 style={{ margin: '0 0 14px 0', fontSize: 16 }}>Documentation</h3>

        {/* Doc tabs */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
          {list.map((doc) => (
            <button
              key={doc.slug}
              type="button"
              onClick={() => { setActiveSlug(doc.slug); setSearch(''); }}
              style={{
                textAlign: 'left', padding: '8px 10px', borderRadius: 6,
                border: '1px solid', borderColor: activeSlug === doc.slug ? '#15172b' : '#e5e7eb',
                background: activeSlug === doc.slug ? '#15172b' : '#fff',
                color: activeSlug === doc.slug ? '#fff' : '#15172b',
                fontSize: 13, cursor: 'pointer',
              }}>
              {doc.title}
              <div style={{ fontSize: 10, opacity: 0.7 }}>{(doc.bytes / 1024).toFixed(1)} KB</div>
            </button>
          ))}
        </div>

        {/* Search */}
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search in this doc…"
          style={{
            width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db',
            fontSize: 13, marginBottom: 14,
          }} />

        {/* TOC */}
        {toc.length > 0 && (
          <>
            <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>On this page</div>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {toc.map((t, i) => (
                <a key={i} href={`#${t.slug}`} onClick={onClickToc(t.slug)}
                   style={{
                     fontSize: 12, padding: '2px 0',
                     paddingLeft: (t.level - 1) * 10,
                     color: t.level === 1 ? '#15172b' : '#525252',
                     fontWeight: t.level <= 2 ? 600 : 400,
                     textDecoration: 'none',
                   }}>
                  {t.text}
                </a>
              ))}
            </nav>
          </>
        )}
      </aside>

      {/* Right: rendered doc */}
      <main style={{ flex: 1, padding: '24px 40px', maxWidth: 980, overflowY: 'auto' }}>
        {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
        {loading ? (
          <div style={{ color: '#888', fontSize: 13 }}>Loading…</div>
        ) : (
          <HighlightWrapper>{renderedNodes}</HighlightWrapper>
        )}
      </main>
    </div>
  );
}

// Exported for tests.
Documentation._test = { highlightInPlace, slugify };
